// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { useTranslate } from "helpers";
import { deleteContactEmailAPI, getContactAPI, getContactEmailsAPI } from "services/api/contacts/api";

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

import {
  ContactEmailSearchPageCmp,
  ContactEmailSearchPageProps,
} from "./types";
import { CONTACT_EMAIL_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import {
  Routes,
} from "ui/modules/contacts/navigation";
import TabMenu from "ui/modules/contacts/pages/components/TabMenu/TabMenu";

// Lokalne komponente
import ContactEmailSearchResults from "./components/ContactEmailSearchResults/ContactEmailSearchResults";
import ContactEmailSearchFilters from "./components/ContactEmailSearchFilters/ContactEmailSearchFilters";

import ContactEmailEditCard from "../ContactEmailEditCard/ContactEmailEditCard";
import { ContactDetailFormValues, ContactEmailRow, ContactEmailSearchFormValues } from "services/api/contacts/types";
import { Dialog } from "ui/components/Dialog";
import { initialContactDetailForm } from "services/api/contacts/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_CONTACT } from "app/components/Sidebar/consts";

const ContactEmailSearchPage: ContactEmailSearchPageCmp = (
  props: ContactEmailSearchPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactEmailRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] = useState<ContactDetailFormValues>(
    initialContactDetailForm
  );

  const [emailId, setEmailId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (id) {
      fetchSearchResult(searchResults.pagination, id);
      fetchContact(id);
    }
  }, [id]);

  const fetchContact = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialContactDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getContactAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);
  };

  const fetchSearchResult = async (pagination: Pagination, contactId: string, name?: string | null) => {
    setIsLoading(true);
    try {
      if (id) {
        const results = await getContactEmailsAPI(pagination, contactId, name);

        setSearchResults(results);
      }
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setEmailId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setEmailId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback((searchFormValues: ContactEmailSearchFormValues) => {
    if (id)
      fetchSearchResult(searchResults.pagination, id, searchFormValues.search);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id)
      fetchSearchResult(newPagination, id);
  };

  const handleEditSuccess = useCallback(() => {
    if (id)
      fetchSearchResult(searchResults.pagination, id);
    setShowAddNewModal(false);
  }, []);

  const handleEmailClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, emailAddress: string) => {
    event.stopPropagation();
    event.preventDefault();
    window.location.href = `mailto:${emailAddress}`;
  }

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setEmailId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!emailId) return;

    try {
      if (id) {
        const deleted = await deleteContactEmailAPI(
          t,
          id,
          emailId,
        );

        if (deleted) {
          await fetchSearchResult(searchResults.pagination, id);
        }

        // zatvori dijalog nakon brisanja
        setOpenDialog(false);
        setEmailId("");
      }

    } catch (err) {
      console.error("Greška prilikom brisanja email-a:", err);
    }
  }, [emailId, t, id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setEmailId("");
    setOpenDialog(false);
  }, []);

  const breadcrumbs: BreadcrumbItem[] = [
    { text: SIDEBAR_ITEM_CONTACT.text, route: SIDEBAR_ITEM_CONTACT.route },
  ];

  if (!id) return <>REDIRECT TODO</>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  {formValues.pictures && formValues.pictures.length > 0 && (
                    <img src={formValues.pictures[0].url} alt={formValues.companyName ?? ""} title={formValues.companyName ?? ""} className="h-12 w-12 object-contain rounded-full" />
                  )}
                  <div>
                    <PageTitle
                      title={`${t(CONTACT_EMAIL_SEARCH_PAGE_TITLE.text)} [${formValues.companyName || `${formValues.firstName} ${formValues.lastName}`}]`}
                      icon={CONTACT_EMAIL_SEARCH_PAGE_TITLE.icon}
                      iconColor={CONTACT_EMAIL_SEARCH_PAGE_TITLE.iconColor}
                    />
                    <Breadcrumb items={breadcrumbs} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center">
              </div>
            </div>
          </PageHeader>
          <TabMenu contactId={id} contactTypeId={formValues?.contactType?.id} />
        </div>
        <div className="px-4 py-4">
          <ContactEmailSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite email adresu klikom na 'Dodaj email adresu'" />
          ) : (
            <ContactEmailSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
              onEmailClick={handleEmailClick}
            />
          )}
        </div>
      </div>
      <ContactEmailEditCard
        show={showAddNewModal}
        emailId={emailId}
        contactId={id}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.contact.email.dialog.delete.title"
        )}
        message={t(
          "admin.contact.email.dialog.delete.message"
        )}
        confirmText={t(
          "admin.contact.email.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.contact.email.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

ContactEmailSearchPage.route = Routes.ContactEmailSearchPageRouter;

export default ContactEmailSearchPage;
